import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import PropTypes from "prop-types";

import {
    calcReport,
    compareReport,
    deleteVariant,
    fetchVariantsByUser,
    saveVariant,
    setFavoritesForVariant,
    setError as setErrorAction,
    setCurrentVariant as setCurrentVariantAction,
    clearFilters,
    generateObjects,
    fetchReportColumns,
    clearReport,
    clearParameters,
    clearVariant,
} from "../../../reducers/reports/userLevelSlice";

import { DIALOG_TYPES, REPORT_LEVEL_MODE } from "../../../utils/sodReports";

import { ProgressDialog, ErrorsDialog, SubmitDialog } from "../../common/dialogs";
import useReduxValue from "../../common/hooks/useReduxValue";

import { FavoritesDialog, SaveVariantDialog, DeleteVariantDialog, VariantsListBox, VariantsListDialog } from "../report-variants";
import ReportActions from "../report-actions";
import { ReportHeader } from "../report-header";

import UserLevelReportParameters from "./user-level-report-parameters.jsx";
import UserLevelReportResults from "./user-level-report-results.jsx";
import GenerateUsersDialog from "../report-parameters/generate-users-dialog.jsx";

const ConfirmSubmitDialog = ({ open, handleSubmitClick, handleCloseClick }) => {
    const intl = useIntl();
    return (
        <SubmitDialog
            open={open}
            title={""}
            message={intl.formatMessage({ id: "user-level.confirm-calc-with-empty-users-field-dialog" })}
            onCancelClick={handleCloseClick}
            buttons={[
                {
                    text: intl.formatMessage({ id: "common.submit-dialog.btn-close" }),
                    type: "close",
                    onClick: handleCloseClick,
                },
                {
                    text: intl.formatMessage({ id: "common.submit-dialog.btn-apply" }),
                    type: "apply",
                    onClick: handleSubmitClick,
                },
            ]}
        />
    );
};

const UserLevelReportPage = () => {
    const { variantName: encodedVariantName } = useParams();
    const variantName = encodedVariantName ? decodeURIComponent(encodedVariantName) : undefined;

    const navigate = useNavigate();

    const dispatch = useDispatch();

    const [error, setError] = useReduxValue((state) => state.reports.userLevel.error, setErrorAction);
    const [currentVariant, setCurrentVariant] = useReduxValue((state) => state.reports.userLevel.currentVariant, setCurrentVariantAction);

    const [variantDialogType, setVariantDialogType] = useState(null);
    const [dialogVariant, setDialogVariant] = useState(null);
    const [confirmSubmitOpen, setConfirmSubmitOpen] = useState(false);

    const users = useSelector((state) => state.reports.userLevel.users);
    const integrIds = useSelector((state) => state.reports.userLevel.integrIds);
    const reportLevelMode = useSelector((state) => state.reports.userLevel.reportLevelMode);
    const busy = useSelector((state) => state.reports.userLevel.busy);
    const busyType = useSelector((state) => state.reports.userLevel.busyType);
    const variantCreated = useSelector((state) => state.reports.userLevel.variantCreated);
    const variants = useSelector((state) => state.reports.userLevel.variants);
    const parametersOpen = useSelector((state) => state.reports.userLevel.parametersOpen);
    const validationErrors = useSelector((state) => state.reports.userLevel.validationErrors);

    const [genUsers, setGenUsers] = useState(false);

    useEffect(() => {
        dispatch(fetchVariantsByUser());

        return () => {
            dispatch(clearFilters());
            dispatch(clearVariant());
            dispatch(clearParameters());
            dispatch(clearReport());
        };
    }, [dispatch]);

    useEffect(() => {
        if (currentVariant && !variantName) {
            dispatch(clearVariant());
            dispatch(clearParameters());
            dispatch(clearReport());
            return;
        }

        if (!currentVariant && !variantName) {
            return;
        }

        if (variantName && currentVariant?.name === variantName) {
            return;
        }
        if (variantName) {
            const newCurrentVariant = variants.find((variant) => variant.name === variantName);

            if (!newCurrentVariant) {
                return;
            }

            dispatch(clearReport());
            setCurrentVariant(newCurrentVariant);
        }
    }, [variantName, dispatch, variants, currentVariant, setCurrentVariant, navigate]);

    useEffect(() => {
        if (variantCreated) {
            hideVariantDialog();
        }
    }, [variantCreated]);

    const hideVariantDialog = () => {
        setDialogVariant(null);
        setVariantDialogType(null);
    };

    // Variants list dialog
    const handleShowVariantsClick = () => {
        setVariantDialogType(DIALOG_TYPES.VARIANTS);
    };

    // Delete dialog
    const handleDeleteDialogConfirmClick = () => {
        setFavoritesForVariant({
            variant: dialogVariant,
            inFavorites: false,
        });
        dispatch(deleteVariant(dialogVariant.name));
        hideVariantDialog();
    };

    const handleDeleteClick = (variant) => {
        setVariantDialogType(DIALOG_TYPES.DELETE);
        setDialogVariant(variant);
    };

    // Favorites dialog
    const handleFavoritesDialogConfirmClick = () => {
        dispatch(
            setFavoritesForVariant({
                variant: dialogVariant,
                inFavorites: !dialogVariant.inFavorites,
            })
        );

        hideVariantDialog();
    };

    const handleFavoriteClick = (variant) => {
        setVariantDialogType(DIALOG_TYPES.FAVORITES);
        setDialogVariant(variant);
    };

    // Save dialog
    const handleSaveDialogConfirmClick = async (variantName) => {
        await dispatch(saveVariant(variantName)).unwrap();
        navigate(`/report-user-level/${encodeURIComponent(variantName)}`);
    };

    const handleSaveClick = () => {
        setVariantDialogType(DIALOG_TYPES.SAVE);
    };

    // Columns settings dialog
    const handleColumnSettingsClick = () => {
        setVariantDialogType(DIALOG_TYPES.COLUMN_SETTINGS);
    };

    const handleSubmitClick = async () => {
        if (!users.length && !integrIds.length && reportLevelMode !== REPORT_LEVEL_MODE.CRITICAL_OBJECTS) {
            setConfirmSubmitOpen(true);
            return;
        }
        if (reportLevelMode == REPORT_LEVEL_MODE.CRITICAL_OBJECTS) {
            // dispatch(clearReport());
            await dispatch(calcReport());
            dispatch(fetchReportColumns());
            return;
        }
        dispatch(clearReport());
        dispatch(calcReport());
    };

    const handleSearchVariant = useCallback(
        (event) => {
            dispatch(fetchVariantsByUser(event.target.value));
        },
        [dispatch]
    );

    const handleVariantClick = (variant) => {
        if (variant.name === variantName) {
            setCurrentVariant(variant);
        } else {
            const encodedVariant = encodeURIComponent(variant.name);

            if (variantName) {
                navigate(`../${encodedVariant}`, { relative: "path" });
            } else {
                navigate(encodedVariant);
            }
        }

        hideVariantDialog();
    };

    const handleCompareReport = () => {
        dispatch(compareReport());
    };

    const variantsListDialogOpen = variantDialogType === DIALOG_TYPES.VARIANTS;
    const saveDialogOpen = variantDialogType === DIALOG_TYPES.SAVE;
    const deleteDialogOpen = dialogVariant && variantDialogType === DIALOG_TYPES.DELETE;
    const favoritesDialogOpen = dialogVariant && variantDialogType === DIALOG_TYPES.FAVORITES;

    return (
        <>
            <ReportHeader
                parameters={<UserLevelReportParameters onShowVariantsClick={handleShowVariantsClick} />}
                variants={
                    <VariantsListBox
                        variants={variants}
                        onDeleteClick={handleDeleteClick}
                        onFavoriteClick={handleFavoriteClick}
                        onItemClick={handleVariantClick}
                        onSearch={handleSearchVariant}
                    />
                }
                parametersOpen={parametersOpen}
            />

            <ReportActions
                onSubmitClick={handleSubmitClick}
                onSaveClick={handleSaveClick}
                onReportCompare={handleCompareReport}
                onColumnSettingsClick={handleColumnSettingsClick}
            />

            <UserLevelReportResults />

            {saveDialogOpen && (
                <SaveVariantDialog
                    defaultName={currentVariant?.name}
                    open={saveDialogOpen}
                    validationErrors={validationErrors}
                    onSaveClick={handleSaveDialogConfirmClick}
                    onCancelClick={hideVariantDialog}
                />
            )}

            {deleteDialogOpen && (
                <DeleteVariantDialog
                    open={deleteDialogOpen}
                    variant={dialogVariant}
                    onDeleteClick={handleDeleteDialogConfirmClick}
                    onCancelClick={hideVariantDialog}
                />
            )}

            {favoritesDialogOpen && (
                <FavoritesDialog
                    open={favoritesDialogOpen}
                    variant={dialogVariant}
                    onFavoritesClick={handleFavoritesDialogConfirmClick}
                    onCancelClick={hideVariantDialog}
                />
            )}

            {variantsListDialogOpen && (
                <VariantsListDialog
                    open={variantsListDialogOpen}
                    variants={variants}
                    onDeleteClick={handleDeleteClick}
                    onFavoriteClick={handleFavoriteClick}
                    onItemClick={handleVariantClick}
                    onSearch={handleSearchVariant}
                    onClose={hideVariantDialog}
                />
            )}

            {reportLevelMode !== REPORT_LEVEL_MODE.CRITICAL_OBJECTS && (
                <ConfirmSubmitDialog
                    open={confirmSubmitOpen}
                    handleCloseClick={() => setConfirmSubmitOpen(false)}
                    handleSubmitClick={() => {
                        setConfirmSubmitOpen(false);
                        dispatch(calcReport());
                    }}
                />
            )}

            <ProgressDialog open={busy} busyType={busyType} />

            <ErrorsDialog error={error} open={error?.type === "error"} onClose={() => setError(null)} />

            <GenerateUsersDialog
                open={genUsers}
                onClose={() => setGenUsers(false)}
                onApply={(template, maxCount) => {
                    dispatch(generateObjects({ template, maxCount }));
                }}
            />
        </>
    );
};

ConfirmSubmitDialog.propTypes = {
    open: PropTypes.bool,
    handleCloseClick: PropTypes.func,
    handleSubmitClick: PropTypes.func,
};

export default UserLevelReportPage;
